import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient from '../services/api';
import { User, AuthResponse } from '../types/auth';

interface AuthContextType {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (authResponse: AuthResponse) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const checkAuth = async () => {
            // Check for SSO Token in URL first
            const urlParams = new URLSearchParams(window.location.search);
            let tokenFromUrl = urlParams.get('token');

            // Handle hash routes or query params placed after hash
            if (!tokenFromUrl && window.location.hash.includes('token=')) {
                try {
                    const hashQueryString = window.location.hash.split('?')[1];
                    if (hashQueryString) {
                        tokenFromUrl = new URLSearchParams(hashQueryString).get('token');
                    }
                } catch (e) { }
            }

            // Also try to get from full href if somehow it was improperly constructed
            if (!tokenFromUrl && window.location.href.includes('token=')) {
                try {
                    const parts = window.location.href.split('token=');
                    if (parts.length > 1) {
                        tokenFromUrl = parts[1].split('&')[0].split('#')[0];
                    }
                } catch (e) { }
            }

            if (tokenFromUrl) {
                localStorage.setItem('auth_token', tokenFromUrl);

                // Clean the URL to prevent token leaking.
                const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);
            }

            const storedToken = localStorage.getItem('auth_token');
            const storedUser = localStorage.getItem('auth_user');

            if (storedToken) {
                try {
                    setToken(storedToken);

                    if (storedUser) {
                        try {
                            const parsedUser = JSON.parse(storedUser);
                            setUser(parsedUser);
                        } catch (e) { /* ignore */ }
                    }

                    // Verify token validity and fetch user profile
                    const response = await apiClient.get('/api/v1/auth/me', {
                        headers: { Authorization: `Bearer ${storedToken}` }
                    });
                    setUser(response.data);
                    localStorage.setItem('auth_user', JSON.stringify(response.data));
                } catch (error) {
                    console.error("Token verification failed", error);
                    logout();
                    if (window.location.pathname !== '/login') {
                        // Force a redirect with query param so Login screen can show explanation
                        window.location.href = '/login?expired=true';
                    }
                }
            }
            setIsLoading(false);
        };

        checkAuth();

        const handleUnauthorized = () => {
            const hasToken = !!localStorage.getItem('auth_token');
            logout();
            if (hasToken && window.location.pathname !== '/login') {
                window.location.href = '/login?expired=true';
            }
        };
        window.addEventListener('auth:unauthorized', handleUnauthorized);

        return () => {
            window.removeEventListener('auth:unauthorized', handleUnauthorized);
        };
    }, []);

    const login = (authResponse: AuthResponse) => {
        setToken(authResponse.access_token);
        setUser(authResponse.user);
        localStorage.setItem('auth_token', authResponse.access_token);
        localStorage.setItem('auth_user', JSON.stringify(authResponse.user));
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
    };

    const value = {
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        logout
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
